import { useState, useEffect, useCallback, useMemo, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { shallow } from "zustand/shallow";

import { jwt } from "../utils/index.js";
import useGlobalState from "../use-global-state.js";
import logo from "../assets/images/Locsys_logo.png";
import logoutIcon from "../assets/menu-icons/Logout.png";
import teamsIcon from "../assets/menu-icons/Collaborator.png";
import assignmentsIcon from "../assets/menu-icons/DSLs.png";
import publicationsIcon from "../assets/menu-icons/Projects.png";
import secretsIcon from "../assets/menu-icons/Secrets.png";
import marketplaceIcon from "../assets/menu-icons/Marketplace.png";
import adminIcon from "../assets/menu-icons/Admin.png";
import generateIcon from "../assets/menu-icons/Generate.png";
import assistantIcon from "../assets/menu-icons/Assistant.png";
import { getDslsConstants } from "../dsls/index.js";
import { getProjects, isUserAdmin } from "../api/index.js";
import { Header as HeaderComponent } from "../tctt.js";
import { theme } from "../theme.js";

// Create icon components OUTSIDE the component so references are stable
const createIconComponent = (src, alt) => {
	const IconComponent = (props) => (
		<img
			src={src}
			alt={alt}
			style={{
				objectFit: "contain",
				width: "100%",
				height: "100%",
				...props.style,
			}}
			className={props.className}
		/>
	);
	IconComponent.displayName = `Icon_${alt}`;
	return IconComponent;
};

// Stable icon component references — created once at module level
const LogoutIcon = createIconComponent(logoutIcon, "Logout");
const TeamsIcon = createIconComponent(teamsIcon, "Collaboration");
const AssignmentsIcon = createIconComponent(assignmentsIcon, "DSLs");
const PublicationsIcon = createIconComponent(publicationsIcon, "Projects");
const SecretsIcon = createIconComponent(secretsIcon, "Secrets");
const MarketplaceIcon = createIconComponent(marketplaceIcon, "Marketplace");
const AdminIcon = createIconComponent(adminIcon, "Admin");
const GenerateIcon = createIconComponent(generateIcon, "Generate");
const AssistantIcon = createIconComponent(assistantIcon, "Assistant");

const Header = ({ isAuthenticated }) => {
	const { logout } = useAuth0();
	const location = useLocation();
	const navigate = useNavigate();

	const { maxDSLScreen } = useGlobalState(
		(e) => ({
			maxDSLScreen: e.maxDSLScreen,
		}),
		shallow
	);

	const [dslsConstants, setDslsConstants] = useState([]);
	const [userProjects, setUserProjects] = useState([]);
	const [isAdmin, setIsAdmin] = useState(false);

	const fetchData = useCallback(async () => {
		if (!jwt.isAuthenticated()) return;
		const constants = await getDslsConstants();
		setDslsConstants(constants);

		const { success: scs } = await isUserAdmin();
		if (scs) {
			setIsAdmin(true);
		}

		const prjs = await getProjects();
		const sortedProjects = prjs.sort((a, b) => {
			if (a.pinned && !b.pinned) return -1;
			if (!a.pinned && b.pinned) return 1;
			return new Date(b.createdAt) - new Date(a.createdAt);
		});

		setUserProjects(sortedProjects);
		console.log("Projects", sortedProjects);
	}, []);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleLogout = useCallback(
		(event) => {
			event.preventDefault();
			jwt.destroyToken();
			logout({
				logoutParams: { returnTo: window.location.origin },
				clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
			});
		},
		[logout]
	);

	// Memoize menu item arrays so they only rebuild when their data changes
	const adminMenuItems = useMemo(
		() => [
			{
				text: "Change modelText for cloned models",
				path: "/admin/model-text",
			},
			{
				text: "Change description for cloned projects",
				path: "/admin/project-description",
			},
		],
		[]
	);

	const projectsMenuItems = useMemo(
		() => [
			{
				text: "All Projects",
				path: "/projects",
			},
			...userProjects.map((prj) => ({
				text: prj?.title,
				path: `/projects/${prj?._id}`,
			})),
		],
		[userProjects]
	);

	const dslsMenuItems = useMemo(
		() => [
			{
				text: "All DSLs",
				path: "/dsls",
			},
			...dslsConstants.map((dsl) => ({
				text: dsl?.name,
				onClick: () => {
					window.location.href = `/dsls/${dsl?._id}`;
				},
			})),
		],
		[dslsConstants]
	);

	// Memoize the entire buttons array so it only rebuilds when dependencies change
	const buttons = useMemo(() => {
		const btns = [];

		// Admin button (conditional)
		if (isAdmin) {
			btns.push({
				key: "admin",
				icon: AdminIcon,
				text: "Admin",
				more: adminMenuItems,
			});
		}

		// Generate button
		btns.push({
			key: "generate",
			icon: GenerateIcon,
			text: "Generate",
			path: "/generate-grammar",
		});

		// Marketplace button
		btns.push({
			key: "marketplace",
			icon: MarketplaceIcon,
			text: "Marketplace",
			path: "/marketplace",
		});

		// AI Assistant button
		btns.push({
			key: "aiassistant",
			icon: AssistantIcon,
			text: "AI Assistant",
			path: "/assistant",
		});

		// Collaboration button
		btns.push({
			key: "collaboration",
			icon: TeamsIcon,
			text: "Collaboration",
			path: "/collaboration",
		});

		// Projects button with dropdown
		btns.push({
			key: "projects",
			icon: PublicationsIcon,
			text: "Projects",
			more: projectsMenuItems,
		});

		// DSLs button with dropdown
		btns.push({
			key: "dsls",
			icon: AssignmentsIcon,
			text: "DSLs",
			more: dslsMenuItems,
		});

		// Secrets button
		btns.push({
			key: "secrets",
			icon: SecretsIcon,
			text: "Secrets",
			path: "/secrets",
		});

		// Logout button
		btns.push({
			key: "logout",
			icon: LogoutIcon,
			text: "Logout",
			onClick: handleLogout,
		});

		return btns;
	}, [isAdmin, adminMenuItems, projectsMenuItems, dslsMenuItems, handleLogout]);

	if (maxDSLScreen) {
		return null;
	}

	return (
		<HeaderComponent
			isAuthenticated={isAuthenticated}
			customTheme={theme}
			location={location}
			navigate={navigate}
			buttons={buttons}
			logo={logo}
			homeLink="/"
			navbarBackground={{
				outer: theme.palette.primary.main,
				inner: "linear-gradient(to bottom, white 0%, #DDE8EF 100%)",
			}}
			breadcrumbsBackground="#ABC7D9"
			containerWidth="100%"
		/>
	);
};

export default memo(Header);
